import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Center, Loader } from '@mantine/core';

import { ClientDto, DocumentDto, ProjectDto } from '../../api/models';
import { useGetApiProjects } from '../../api/projects/projects.ts';
import DocumentAddModal from '../Documents/DocumentAddModal.tsx';
import ProjectAddModal from '../Projects/ProjectAddModal.tsx';
import { getDevOpsEditorRoute, getEditorRoute } from '../utils/RouteResolverUtils.tsx';
import ServerLoadErrorFallback from '../utils/ServerLoadErrorFallback.tsx';
import { useProjectData } from '../utils/useProjectData.tsx';
import NavLinksGroup from './NavLinksGroup.tsx';

export interface NavLinksGroupItem {
    label: string;
    id: string;
    documents?: DocumentDto[];
    project: ProjectDto;
    client?: ClientDto;
}

interface SelectedDocumentInfo {
    document: DocumentDto;
    projectName: string;
}

interface DocumentOverviewProps {
    selectedDocumentInfo?: SelectedDocumentInfo;
    onSelectedDocumentInfoChange?: (
        info: SelectedDocumentInfo | undefined
    ) => void;
    onBeforeNavigate?: () => boolean; // Returns true if navigation should proceed
}

const DocumentOverview = ({
    selectedDocumentInfo,
    onSelectedDocumentInfoChange,
    onBeforeNavigate
}: DocumentOverviewProps) => {
    const { setRefetchProjects } = useProjectData();
    const [linksData, setLinksData] = useState<NavLinksGroupItem[]>([]);
    const { data, isLoading, error, refetch } = useGetApiProjects();
    const { documentId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Extract projectId from DevOps routes (e.g. /devops-editor/:projectId/*)
    const devOpsProjectId = location.pathname.startsWith('/devops-editor/')
        ? location.pathname.split('/')[2]
        : undefined;
    const [openedProjects, setOpenedProjects] = useState<
        Record<string, boolean>
    >({});
    const [addDocumentProject, setAddDocumentProject] =
        useState<ProjectDto | null>(null);

    useEffect(() => {
        setRefetchProjects(async () => {
            await refetch();
        });
    }, [refetch, setRefetchProjects]);

    useEffect(() => {
        if (!Array.isArray(data)) return;

        const opened: Record<string, boolean> = {};

        const mapped: NavLinksGroupItem[] = data.map(p => {
            const isOpen = p.documents.some(
                (document: DocumentDto) => document.id === documentId
            );
            if (isOpen) opened[p.id] = true;

            // Also expand project when viewing a DevOps file from that project
            if (devOpsProjectId && p.id === devOpsProjectId) opened[p.id] = true;

            return {
                id: p.id,
                label: p.name,
                client: p.client,
                documents: p.documents,
                project: p
            };
        });

        setLinksData(mapped);
        setOpenedProjects(opened);

        // Sync selected document with URL parameter
        if (documentId && data) {
            for (const project of data) {
                const currentDocument = project.documents.find(
                    d => d.id === documentId
                );
                if (currentDocument) {
                    onSelectedDocumentInfoChange?.({
                        document: currentDocument,
                        projectName: project.name
                    });
                    break;
                }
            }
        } else if (!documentId) {
            onSelectedDocumentInfoChange?.(undefined);
        }
    }, [data, documentId, devOpsProjectId, onSelectedDocumentInfoChange]);

    const safeRefetch = async (): Promise<ProjectDto[]> => {
        const result = await refetch();
        if (result.data) return result.data;
        throw result.error ?? new Error('Unknown error during refetch');
    };

    const handleDocumentDeleted = (document: DocumentDto) => {
        if (selectedDocumentInfo?.document.id === document.id) {
            closeDocumentEditor();
        }
    };

    const handleProjectArchived = (project: ProjectDto) => {
        if (
            selectedDocumentInfo !== undefined &&
            project.documents.some(
                (d: DocumentDto) => d.id === selectedDocumentInfo.document.id
            )
        ) {
            closeDocumentEditor();
        }
    };

    const closeDocumentEditor = () => {
        // Check for unsaved changes before navigating
        if (onBeforeNavigate && !onBeforeNavigate()) {
            return; // Navigation was cancelled
        }

        onSelectedDocumentInfoChange?.(undefined);
        void navigate('/');
    };

    const openDocumentEditor = (document: DocumentDto, projectName: string) => {
        // Check for unsaved changes before navigating
        if (onBeforeNavigate && !onBeforeNavigate()) {
            return; // Navigation was cancelled
        }

        onSelectedDocumentInfoChange?.({
            document,
            projectName
        });
        void navigate(getEditorRoute(document.id));
    };

    const openDevOpsFile = (projectId: string, filePath: string) => {
        if (onBeforeNavigate && !onBeforeNavigate()) {
            return;
        }
        onSelectedDocumentInfoChange?.(undefined);
        void navigate(getDevOpsEditorRoute(projectId, filePath));
    };

    const handleShowAddDocument = (project: ProjectDto) => {
        setAddDocumentProject(project);
    };

    const handleDocumentCreatedFromEmpty = (document: DocumentDto) => {
        if (addDocumentProject) {
            setAddDocumentProject(null);
            openDocumentEditor(document, addDocumentProject.name);
        }
    };

    if (isLoading)
        return (
            <Center p='md'>
                <Loader size='sm' />
            </Center>
        );
    if (error)
        return (
            <ServerLoadErrorFallback
                entity={'Project Overview'}
                error={error}
            />
        );

    return (
        <>
            {linksData.map(proj => (
                <div key={proj.project.id}>
                    <NavLinksGroup
                        {...proj}
                        refetch={safeRefetch}
                        opened={openedProjects[proj.project.id]}
                        onDocumentDeleted={handleDocumentDeleted}
                        onProjectArchived={handleProjectArchived}
                        onDocumentCreated={document =>
                            openDocumentEditor(document, proj.project.name)
                        }
                        onDocumentSelected={document =>
                            openDocumentEditor(document, proj.project.name)
                        }
                        onShowAddDocument={handleShowAddDocument}
                        onDevOpsFileSelected={(filePath) => openDevOpsFile(proj.project.id, filePath)}
                        toggleOpened={() =>
                            setOpenedProjects(prev => ({
                                ...prev,
                                [proj.project.id]: !prev[proj.project.id]
                            }))
                        }
                    />
                </div>
            ))}
            <div
                style={{
                    padding:
                        'var(--mantine-spacing-xs) var(--mantine-spacing-md)'
                }}
            >
                <ProjectAddModal
                    refetch={safeRefetch}
                    existingProjects={data}
                />
            </div>

            {addDocumentProject && (
                <DocumentAddModal
                    project={addDocumentProject}
                    opened={!!addDocumentProject}
                    close={() => setAddDocumentProject(null)}
                    onDocumentCreated={handleDocumentCreatedFromEmpty}
                    refetch={safeRefetch}
                />
            )}
        </>
    );
};

export default DocumentOverview;
